import panelApi from "@/lib/panelApi";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const has = (s, ...keys) => keys.some((k) => s.includes(k));

// Coluna do modelo para cada componente; null = coluna extra no final.
const colOf = (it) => {
  const l = norm(it.label);
  const src = it.source || "";
  if ((it.cls === "tax" || it.cls === "discount") && has(l, "inss")) return "N";
  if (it.cls === "tax") return null;
  if (it.cls === "discount") return /\bva\b/.test(l) || has(l, "vale alimenta", "refei", "aliment") ? "P" : "O";
  if (it.isBaseSalary) return "E";
  if (has(l, "pericul")) return "F";
  if (src === "fuel" || has(l, "combust")) return "G";
  if (src === "commute" || has(l, "passag", "desloc", "transporte")) return "H";
  if (src === "meal" || has(l, "aliment", "refei")) return "I";
  if (has(l, "manuten", "oleo")) return "J";
  if (has(l, "seguro")) return "K";
  if (has(l, "familia")) return "L";
  if (it.auto || src.startsWith("bonus") || has(l, "bonifica", "produtiv", "bonus")) return "M";
  if (it.cls === "salary" && has(l, "salario base", "salario")) return "E";
  return null;
};

export const toXlsxRow = (base, items) => {
  const cols = {};
  const extras = {};
  items.forEach((it) => {
    const v = Number(it.value) || 0;
    if (!v) return;
    const c = colOf(it);
    if (c) { cols[c] = (cols[c] || 0) + v; return; }
    const desc = it.cls === "tax" || it.cls === "discount";
    const k = `${desc ? "d" : "p"}|${it.label}`;
    (extras[k] = extras[k] || { label: it.label || "Outros", desc, value: 0 }).value += v;
  });
  return { ...base, cols, extras: Object.values(extras) };
};

export const vehicleOf = (l) => (l.role === "funcionario" ? "ADMIN" : l.vehicle_type === "motociclista" ? "Motoqueiro" : "Ciclista");

const pad = (n) => String(n).padStart(2, "0");
export const periodText = (month, period, baseLabel) => {
  const [y, m] = month.split("-").map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const [a, b] = period === 1 ? [1, 15] : period === 2 ? [16, lastDay] : [1, lastDay];
  return `Período Operacional: ${pad(a)}/${pad(m)}/${y} a ${pad(b)}/${pad(m)}/${y} | Base: ${baseLabel}`;
};

const saveBlob = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

const fetchXlsx = async (payload) => {
  if (!payload.rows.length) { toast.error("Nenhum colaborador CLT/Administrativo no período"); return null; }
  try {
    return (await panelApi.post("/panel/payroll-xlsx", payload, { responseType: "blob" })).data;
  } catch (e) {
    let msg = apiError(e);
    try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* mantém */ }
    toast.error(msg);
    return null;
  }
};

export const downloadPayrollXlsx = async (payload, fileName) => {
  const blob = await fetchXlsx(payload);
  if (!blob) return;
  saveBlob(blob, fileName);
  toast.success("Excel gerado");
};

// Compartilha os arquivos anexados pelo menu do sistema (escolher WhatsApp e o contato).
// Sem suporte no navegador: baixa os arquivos e abre o WhatsApp com a mensagem.
export const shareFiles = async (files, text, waUrl) => {
  const objs = files.map((f) => new File([f.blob], f.name, { type: f.blob.type || "application/pdf" }));
  if (navigator.canShare && navigator.canShare({ files: objs })) {
    try { await navigator.share({ files: objs, text }); return; } catch (e) { if (e.name === "AbortError") return; }
  }
  files.forEach((f, i) => setTimeout(() => saveBlob(f.blob, f.name), i * 400));
  window.open(waUrl, "_blank");
  toast.info("Este navegador não permite anexar direto. Os arquivos foram baixados para você anexar no WhatsApp.");
};

const CSV_HEAD = ["Funcionário", "Veículo", "Contrato", "Entregas", "Salário Base", "Periculidade", "Combustível", "Passagem", "Alimentação", "Manuntenção Oléo", "Seguro de vida", "Salário Família", "Bonificação por Entregas (R$)", "INSS", "Descontos", "Descontos VA", "Total Líquido", "Total Benefícios", "Férias Proporcionais (1/12)", "Décimo Proporcional (1/12)", "Terço de Férias (1/36)"];
const DATA_COLS = "EFGHIJKLMNOP".split("");
const cell = (v) => (typeof v === "number" ? v.toFixed(2).replace(".", ",") : `"${String(v ?? "").replace(/"/g, '""')}"`);

// Mesma planilha do "Exportar Excel" em CSV (padrão Excel BR: ";" e vírgula decimal), com os
// totais das fórmulas do modelo já calculados, pois CSV não guarda fórmulas.
const buildPayrollCsv = (payload) => {
  const extras = [];
  payload.rows.forEach((r) => (r.extras || []).forEach((e) => { if (!extras.some((x) => x.label === e.label && x.desc === e.desc)) extras.push({ label: e.label, desc: e.desc }); }));
  const tot = new Array(18 + extras.length).fill(0);
  const lines = payload.rows.map((r) => {
    const c = Object.fromEntries(DATA_COLS.map((k) => [k, Number(r.cols?.[k]) || 0]));
    const ex = extras.map((x) => Number((r.extras || []).find((e) => e.label === x.label && e.desc === x.desc)?.value) || 0);
    const liq = "EFGHIJKLM".split("").reduce((a, k) => a + c[k], 0) - c.N - c.O - c.P + ex.reduce((a, v, i) => a + (extras[i].desc ? -v : v), 0);
    const nums = [Number(r.deliveries) || 0, ...DATA_COLS.map((k) => c[k]), liq, c.G + c.H + c.I + c.J + c.M, c.E / 12, c.E / 12, c.E / 36, ...ex];
    nums.forEach((v, i) => { tot[i] += v; });
    return [r.name, r.vehicle, r.contract, r.deliveries ?? "", ...nums.slice(1)].map(cell).join(";");
  });
  const head = [...CSV_HEAD, ...extras.map((x) => x.label)].map(cell).join(";");
  const total = ["TOTAL GERAL", "", "", ...tot].map(cell).join(";");
  const body = [cell(payload.period_text), "", head, ...lines, "", total].join("\r\n");
  return new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), new TextEncoder().encode(body)], { type: "text/csv" });
};

// Excel: o navegador não anexa .xlsx no compartilhamento, então vai anexado como CSV.
export const shareXlsxWhatsApp = (payload, fileName, text, phone = "") => {
  if (!payload.rows.length) { toast.error("Planilha disponível só para colaboradores CLT/Administrativo"); return; }
  shareFiles([{ blob: buildPayrollCsv(payload), name: fileName.replace(/\.xlsx$/, ".csv") }], text, `https://wa.me/${phone}?text=${encodeURIComponent(text)}`);
};

// Navegadores só anexam PDF/imagem no compartilhamento (planilha .xlsx é recusada), e o share precisa
// acontecer no mesmo clique, sem await antes. Por isso envia só os PDFs, gerados na hora.
export const sharePayrollWhatsApp = ({ pdfs, text }) => {
  const files = pdfs.filter(Boolean).map((p) => ({ blob: p.doc.output("blob"), name: p.name }));
  return shareFiles(files, text, `https://wa.me/?text=${encodeURIComponent(text)}`);
};
